/**
 * The portfolio, one row per plat. Debt coverage, cap rate and NOI lead, as a
 * lender reads them; they are the plat statement's own cells. Net income,
 * rent and collections are the plat's houses added up. What needs a look is
 * flagged under the plat's name, and a click anywhere on the row opens its
 * houses. Columns added from the Analyst rail follow, each plat's average
 * house (or total) as the column says.
 */

import { ChevronRight } from "lucide-react";
import React from "react";
import type { AddedColumn } from "../../services/portfolioDataService";
import {
	type PlatRow,
	addedAggregate,
	formatAdded,
	formatMoney,
	formatPlatFact,
	formatValue,
} from "./portfolioModel";

export interface PlatTableProps {
	rows: readonly PlatRow[];
	onOpen: (platId: string) => void;
	/** Plats opened in place, showing their houses under the row. */
	expanded: ReadonlySet<string>;
	onToggle: (platId: string) => void;
	renderHouses: (platId: string) => React.ReactNode;
	/** Columns added from the Analyst rail, and the year they are read for. */
	added?: readonly AddedColumn[];
	year?: number;
	/** Each plat's house ids, for an added column's plat figure. */
	housesOf?: (platId: string) => string[];
}

const TH = "border-b border-gray-200 bg-[#fafafa] px-2.5 py-2 align-top";
const TD = "whitespace-nowrap border-b border-[#f1f2f4] px-2.5 py-2.5 text-[#1F2937]";
const NUM = "text-right tabular-nums";
const neg = (value: number | null | undefined) =>
	value !== null && value !== undefined && value < 0 ? "text-[#b42318]" : "";

const COLUMNS: { label: string; unit: string }[] = [
	{ label: "DCR", unit: "ratio" },
	{ label: "Cap rate", unit: "on assessed" },
	{ label: "NOI", unit: "$ / yr" },
	{ label: "Net income", unit: "$ / yr" },
	{ label: "Net after debt & capex", unit: "$ / yr" },
	{ label: "Rent billed", unit: "$ / yr" },
	{ label: "Rent collected", unit: "$ / yr" },
	{ label: "Vacancy loss", unit: "% of billed" },
];

const sum = (rows: readonly PlatRow[], pick: (row: PlatRow) => number | null) => {
	const values = rows.map(pick).filter((v): v is number => v !== null);
	return values.length ? values.reduce((a, b) => a + b, 0) : null;
};

const PlatTable: React.FC<PlatTableProps> = ({
	rows,
	onOpen,
	expanded,
	onToggle,
	renderHouses,
	added = [],
	year = 0,
	housesOf = () => [],
}) => {
	const allHouses = rows.flatMap((row) => housesOf(row.platId));
	const billed = sum(rows, (r) => r.billed);
	const collected = sum(rows, (r) => r.collected);
	const net = sum(rows, (r) => r.netIncome);
	const after = sum(rows, (r) => r.afterPrincipal);
	const noi = sum(rows, (r) => r.noi?.value ?? null);
	return (
		<div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
			<table className="w-full border-collapse text-[13px]">
				<thead>
					<tr className="text-left">
						<th scope="col" className={TH}>
							<span className="text-[12.5px] font-semibold text-[#1F2937]">Plat</span>
						</th>
						{COLUMNS.map((column) => (
							<th key={column.label} scope="col" className={`${TH} text-right`}>
								<span className="block text-[12.5px] font-semibold leading-tight text-[#1F2937]">{column.label}</span>
								{column.unit && <span className="block text-[11px] font-normal text-[#6B7280]">{column.unit}</span>}
							</th>
						))}
						{added.map((column) => (
							<th key={column.id} scope="col" data-added-column={column.id} className={`${TH} bg-[#f5f6ff] text-right`} title={column.definition}>
								<span className="block text-[12.5px] font-semibold leading-tight text-[#1F2937]">{column.label}</span>
								<span className="block text-[11px] font-normal text-[#6B7280]">
									{column.unit}
									<span className="font-semibold text-[#3538cd]"> · added in chat</span>
								</span>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<React.Fragment key={row.platId}>
						<tr onClick={() => onOpen(row.platId)} className="cursor-pointer hover:bg-[#f6fbfb]">
							<td className={TD}>
								<div className="flex items-start gap-1.5">
								<button
									type="button"
									onClick={(event) => {
										event.stopPropagation();
										onToggle(row.platId);
									}}
									aria-expanded={expanded.has(row.platId)}
									aria-label={`${expanded.has(row.platId) ? "Hide" : "Show"} ${row.platName}'s houses here`}
									className="-ml-1 mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded text-[#5B6472] hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]"
								>
									<ChevronRight
										className={`h-4 w-4 transition-transform ${expanded.has(row.platId) ? "rotate-90" : ""}`}
										aria-hidden
									/>
								</button>
								<div className="min-w-0">
								<button
									type="button"
									onClick={(event) => {
										event.stopPropagation();
										onOpen(row.platId);
									}}
									className="text-left font-semibold text-[#004C4B] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]"
								>
									{row.platName}
								</button>
								<div className="text-xs text-[#6B7280]">
									{row.houses} houses · {row.bank} loan{row.rates ? ` · ${row.rates}` : ""}
								</div>
								{row.flags.length > 0 && (
									<div className="mt-1 flex flex-wrap gap-1">
										{row.flags.map((flag) => (
											<span
												key={flag}
												className="inline-flex h-[20px] items-center rounded-full bg-[#fffaeb] px-2 text-[11px] font-semibold text-[#b54708]"
											>
												{flag}
											</span>
										))}
									</div>
								)}
								</div>
								</div>
							</td>
							<td className={`${TD} ${NUM} font-semibold ${(row.dcr?.value ?? 1) < 1 ? "text-[#b42318]" : ""}`}>
								{formatPlatFact(row.dcr)}
							</td>
							<td className={`${TD} ${NUM}`}>{formatPlatFact(row.capRate)}</td>
							<td className={`${TD} ${NUM}`}>{formatPlatFact(row.noi)}</td>
							<td className={`${TD} ${NUM} ${neg(row.netIncome)}`}>{formatMoney(row.netIncome)}</td>
							<td className={`${TD} ${NUM} ${neg(row.afterPrincipal)}`}>{formatMoney(row.afterPrincipal)}</td>
							<td className={`${TD} ${NUM}`}>{formatMoney(row.billed)}</td>
							<td className={`${TD} ${NUM}`}>{formatMoney(row.collected)}</td>
							<td className={`${TD} ${NUM}`}>{formatValue(row.vacancyPct, "pct")}</td>
							{added.map((column) => (
								<td key={column.id} className={`${TD} ${NUM} bg-[#fafaff]`}>
									{column.aggregate === "none"
										? "—"
										: formatAdded(addedAggregate(column, housesOf(row.platId), year), column)}
									{column.aggregate === "mean" && (
										<span className="ml-1 text-[11px] text-[#6B7280]">avg</span>
									)}
								</td>
							))}
						</tr>
						{expanded.has(row.platId) && (
							<tr>
								<td colSpan={COLUMNS.length + added.length + 1} className="border-b border-gray-200 bg-[#f7f8f8] px-3 py-3">
									{renderHouses(row.platId)}
								</td>
							</tr>
						)}
						</React.Fragment>
					))}
					<tr className="bg-[#fafafa] font-semibold">
						<td className={TD}>
							Portfolio
							<div className="text-xs font-normal text-[#6B7280]">{rows.reduce((a, r) => a + r.houses, 0)} houses</div>
						</td>
						<td className={`${TD} ${NUM} font-normal text-[#6B7280]`}>by plat</td>
						<td className={`${TD} ${NUM} font-normal text-[#6B7280]`}>by plat</td>
						<td className={`${TD} ${NUM}`}>{formatMoney(noi)}</td>
						<td className={`${TD} ${NUM} ${neg(net)}`}>{formatMoney(net)}</td>
						<td className={`${TD} ${NUM} ${neg(after)}`}>{formatMoney(after)}</td>
						<td className={`${TD} ${NUM}`}>{formatMoney(billed)}</td>
						<td className={`${TD} ${NUM}`}>{formatMoney(collected)}</td>
						<td className={`${TD} ${NUM}`}>
							{formatValue(billed && collected !== null ? (billed - collected) / billed : null, "pct")}
						</td>
						{added.map((column) => (
							<td key={column.id} className={`${TD} ${NUM}`}>
								{column.aggregate === "none" ? "" : formatAdded(addedAggregate(column, allHouses, year), column)}
								{column.aggregate === "mean" && (
									<span className="ml-1 text-[11px] font-normal text-[#6B7280]">avg</span>
								)}
							</td>
						))}
					</tr>
				</tbody>
			</table>
		</div>
	);
};

export default PlatTable;
